import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  getClientPortalDashboard, 
  getMyQuotationRequests, 
  createQuotationRequest,
  getMyQuotationRequest,
  addClientMessage,
} from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { StatusBadge } from '../components/app';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import {
  Briefcase,
  Receipt,
  FileText,
  CurrencyDollar,
  Clock,
  Check,
  SignOut,
  Plus,
  PaperPlaneTilt,
  ChatText,
  ArrowLeft,
  CalendarBlank,
  Phone,
  Envelope,
  Buildings,
  User,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_team-pulse-119/artifacts/yh3w88f3_logo.png';

// Request Detail Component
function RequestDetailView({ requestId, onBack }) {
  const { t, isSpanish } = useLanguage();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  const fetchRequest = useCallback(async () => {
    try {
      const response = await getMyQuotationRequest(requestId);
      setRequest(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [requestId, t]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      await addClientMessage(requestId, newMessage);
      setNewMessage('');
      toast.success(isSpanish ? 'Mensaje enviado' : 'Message sent');
      fetchRequest();
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setSending(false);
    }
  };

  if (loading || !request) {
    return <div className="text-center py-8"><div className="animate-spin w-8 h-8 border-2 border-[#E2B05E] border-t-transparent rounded-full mx-auto" /></div>;
  }

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="flex items-center gap-2 text-[#9CA3AF] hover:text-white transition-colors">
        <ArrowLeft className="w-4 h-4" />
        {isSpanish ? 'Volver a solicitudes' : 'Back to requests'}
      </button>

      {/* Header */}
      <div className="bg-[#151B26] border border-[#242E42] rounded-xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[#E2B05E] font-mono text-sm mb-1">{request.request_number}</p>
            <h2 className="font-['Outfit'] text-xl font-bold text-white">{request.title}</h2>
          </div>
          <StatusBadge value={request.status} />
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-[#9CA3AF] mb-1">{isSpanish ? 'Tipo de Servicio' : 'Service Type'}</p>
            <p className="text-white">{request.service_type}</p>
          </div>
          <div>
            <p className="text-[#9CA3AF] mb-1">{isSpanish ? 'Prioridad' : 'Priority'}</p>
            <p className="text-white">{request.priority}</p>
          </div>
          {request.desired_delivery_date && (
            <div>
              <p className="text-[#9CA3AF] mb-1">{isSpanish ? 'Fecha Deseada' : 'Desired Date'}</p>
              <p className="text-white">{request.desired_delivery_date}</p>
            </div>
          )}
          {request.estimated_budget && (
            <div>
              <p className="text-[#9CA3AF] mb-1">{isSpanish ? 'Presupuesto Est.' : 'Est. Budget'}</p>
              <p className="text-white">${request.estimated_budget.toLocaleString()}</p>
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="bg-[#151B26] border border-[#242E42] rounded-xl p-6">
        <h3 className="font-['Outfit'] font-semibold text-white mb-3">{isSpanish ? 'Descripción' : 'Description'}</h3>
        <p className="text-[#9CA3AF] whitespace-pre-wrap">{request.description}</p>
        {request.additional_notes && (
          <>
            <h4 className="font-semibold text-white mt-4 mb-2">{isSpanish ? 'Notas Adicionales' : 'Additional Notes'}</h4>
            <p className="text-[#9CA3AF]">{request.additional_notes}</p>
          </>
        )}
      </div>

      {/* Linked Quotation */}
      {request.quotation && (
        <div className="bg-[#151B26] border border-[#3AB8D6]/30 rounded-xl p-6">
          <h3 className="font-['Outfit'] font-semibold text-[#3AB8D6] mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {isSpanish ? 'Cotización Generada' : 'Generated Quotation'}
          </h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#E2B05E] font-mono">{request.quotation.quotation_number}</p>
              <p className="text-2xl font-bold text-white mt-1">${request.quotation.total?.toLocaleString()}</p>
            </div>
            <StatusBadge value={request.quotation.status} />
          </div>
        </div>
      )}

      {/* Communication */}
      <div className="bg-[#151B26] border border-[#242E42] rounded-xl p-6">
        <h3 className="font-['Outfit'] font-semibold text-white mb-4 flex items-center gap-2">
          <ChatText className="w-5 h-5 text-[#3AB8D6]" />
          {isSpanish ? 'Comunicación' : 'Communication'}
        </h3>
        
        {request.messages && request.messages.length > 0 ? (
          <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
            {request.messages.map((msg, idx) => (
              <div key={idx} className={`p-4 rounded-lg ${msg.sender === 'equipo' ? 'bg-[#3AB8D6]/10 border-l-4 border-[#3AB8D6]' : 'bg-[#E2B05E]/10 border-l-4 border-[#E2B05E]'}`}>
                <div className="flex justify-between mb-2">
                  <span className="font-medium text-white">{msg.sender === 'equipo' ? 'Equipo Valhalla' : msg.sender_name}</span>
                  <span className="text-xs text-[#6B7280]">{msg.sent_at?.split('T')[0]}</span>
                </div>
                <p className="text-[#9CA3AF]">{msg.message}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#9CA3AF] text-center py-4 mb-6">{isSpanish ? 'Sin mensajes aún' : 'No messages yet'}</p>
        )}

        {/* Reply form - only if status allows */}
        {request.status === 'requiere_mas_info' && (
          <div className="space-y-3 border-t border-[#242E42] pt-4">
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={isSpanish ? 'Escriba su respuesta...' : 'Write your response...'}
              className="bg-[#0B0E14] border-[#242E42] text-white placeholder:text-[#6B7280] min-h-24"
            />
            <Button onClick={handleSendMessage} disabled={!newMessage.trim() || sending} className="bg-[#E2B05E] text-[#0B0E14] hover:bg-[#F0C47A]">
              <PaperPlaneTilt className="w-4 h-4 mr-2" />
              {sending ? (isSpanish ? 'Enviando...' : 'Sending...') : (isSpanish ? 'Enviar Respuesta' : 'Send Response')}
            </Button>
          </div>
        )}
      </div>

      {/* Status History */}
      <div className="bg-[#151B26] border border-[#242E42] rounded-xl p-6">
        <h3 className="font-['Outfit'] font-semibold text-white mb-4">{isSpanish ? 'Historial' : 'History'}</h3>
        <div className="space-y-3">
          {request.status_history?.slice().reverse().map((entry, idx) => (
            <div key={idx} className="flex items-start gap-3 py-2 border-b border-[#242E42]/50 last:border-0">
              <div className="w-2 h-2 rounded-full mt-2 bg-[#3AB8D6]"></div>
              <div>
                <StatusBadge value={entry.status} />
                <p className="text-sm text-[#9CA3AF] mt-1">{entry.comment}</p>
                <p className="text-xs text-[#6B7280]">{entry.changed_at?.split('T')[0]}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// New Request Form Component
function NewRequestForm({ onSuccess, onCancel, clientName }) {
  const { t, isSpanish } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    service_type: 'desarrollo',
    priority: 'normal',
    desired_delivery_date: '',
    estimated_budget: '',
    contact_name: user?.name || '',
    contact_email: user?.email || '',
    contact_phone: '',
    company_name: clientName || '',
    additional_notes: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error(isSpanish ? 'Complete los campos requeridos' : 'Fill required fields');
      return;
    }
    
    setLoading(true);
    try {
      const data = { ...formData };
      if (data.estimated_budget) data.estimated_budget = parseFloat(data.estimated_budget);
      else delete data.estimated_budget;
      if (!data.desired_delivery_date) delete data.desired_delivery_date;
      
      await createQuotationRequest(data);
      toast.success(isSpanish ? 'Solicitud enviada exitosamente' : 'Request submitted successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          <div>
            <Label className="text-[#9CA3AF]">{isSpanish ? 'Título de la Solicitud *' : 'Request Title *'}</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder={isSpanish ? 'Ej: Desarrollo de aplicación web' : 'Ex: Web application development'}
              className="bg-[#0B0E14] border-[#242E42] text-white"
              required
            />
          </div>
          
          <div>
            <Label className="text-[#9CA3AF]">{isSpanish ? 'Tipo de Servicio' : 'Service Type'}</Label>
            <Select value={formData.service_type} onValueChange={(v) => setFormData({ ...formData, service_type: v })}>
              <SelectTrigger className="bg-[#0B0E14] border-[#242E42] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#151B26] border-[#242E42]">
                <SelectItem value="desarrollo">{isSpanish ? 'Desarrollo' : 'Development'}</SelectItem>
                <SelectItem value="mantenimiento">{isSpanish ? 'Mantenimiento' : 'Maintenance'}</SelectItem>
                <SelectItem value="soporte">{isSpanish ? 'Soporte' : 'Support'}</SelectItem>
                <SelectItem value="diseno">{isSpanish ? 'Diseño' : 'Design'}</SelectItem>
                <SelectItem value="consultoria">{isSpanish ? 'Consultoría' : 'Consulting'}</SelectItem>
                <SelectItem value="otro">{isSpanish ? 'Otro' : 'Other'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="text-[#9CA3AF]">{isSpanish ? 'Prioridad' : 'Priority'}</Label>
            <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
              <SelectTrigger className="bg-[#0B0E14] border-[#242E42] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#151B26] border-[#242E42]">
                <SelectItem value="baja">{isSpanish ? 'Baja' : 'Low'}</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="alta">{isSpanish ? 'Alta' : 'High'}</SelectItem>
                <SelectItem value="urgente">{isSpanish ? 'Urgente' : 'Urgent'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="text-[#9CA3AF]">{isSpanish ? 'Fecha Deseada de Entrega' : 'Desired Delivery Date'}</Label>
            <Input
              type="date"
              value={formData.desired_delivery_date}
              onChange={(e) => setFormData({ ...formData, desired_delivery_date: e.target.value })}
              className="bg-[#0B0E14] border-[#242E42] text-white"
            />
          </div>
          
          <div>
            <Label className="text-[#9CA3AF]">{isSpanish ? 'Presupuesto Estimado (USD)' : 'Estimated Budget (USD)'}</Label>
            <Input
              type="number"
              value={formData.estimated_budget}
              onChange={(e) => setFormData({ ...formData, estimated_budget: e.target.value })}
              placeholder="5000"
              className="bg-[#0B0E14] border-[#242E42] text-white"
            />
          </div>
        </div>

        {/* Right Column - Contact Info */}
        <div className="space-y-4">
          <div>
            <Label className="text-[#9CA3AF]">{isSpanish ? 'Nombre de Contacto *' : 'Contact Name *'}</Label>
            <Input
              value={formData.contact_name}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              className="bg-[#0B0E14] border-[#242E42] text-white"
              required
            />
          </div>
          
          <div>
            <Label className="text-[#9CA3AF]">{isSpanish ? 'Email de Contacto *' : 'Contact Email *'}</Label>
            <Input
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              className="bg-[#0B0E14] border-[#242E42] text-white"
              required
            />
          </div>
          
          <div>
            <Label className="text-[#9CA3AF]">{isSpanish ? 'Teléfono' : 'Phone'}</Label>
            <Input
              value={formData.contact_phone}
              onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              placeholder="+507 6XXX-XXXX"
              className="bg-[#0B0E14] border-[#242E42] text-white"
            />
          </div>
          
          <div>
            <Label className="text-[#9CA3AF]">{isSpanish ? 'Empresa' : 'Company'}</Label>
            <Input
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="bg-[#0B0E14] border-[#242E42] text-white"
            />
          </div>
        </div>
      </div>

      {/* Description */}
      <div>
        <Label className="text-[#9CA3AF]">{isSpanish ? 'Descripción del Requerimiento *' : 'Requirement Description *'}</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder={isSpanish ? 'Describa detalladamente lo que necesita...' : 'Describe in detail what you need...'}
          className="bg-[#0B0E14] border-[#242E42] text-white min-h-32"
          required
        />
      </div>

      {/* Additional Notes */}
      <div>
        <Label className="text-[#9CA3AF]">{isSpanish ? 'Notas Adicionales' : 'Additional Notes'}</Label>
        <Textarea
          value={formData.additional_notes}
          onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
          placeholder={isSpanish ? 'Información adicional, referencias, etc.' : 'Additional info, references, etc.'}
          className="bg-[#0B0E14] border-[#242E42] text-white min-h-20"
        />
      </div>

      {/* Actions */}
      <div className="flex gap-4">
        <Button type="button" variant="outline" onClick={onCancel} className="border-[#242E42] text-[#9CA3AF]">
          {t('cancel')}
        </Button>
        <Button type="submit" disabled={loading} className="bg-[#E2B05E] text-[#0B0E14] hover:bg-[#F0C47A] flex-1">
          <PaperPlaneTilt className="w-4 h-4 mr-2" />
          {loading ? (isSpanish ? 'Enviando...' : 'Submitting...') : (isSpanish ? 'Enviar Solicitud' : 'Submit Request')}
        </Button>
      </div>
    </form>
  );
}

export function ClientPortalPage() {
  const { t, isSpanish } = useLanguage();
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [myRequests, setMyRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeTab === 'requests') fetchMyRequests(); }, [activeTab]);

  const fetchData = async () => {
    try {
      const res = await getClientPortalDashboard();
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setData(null);
      } else { toast.error(t('errorOccurred')); }
    } finally { setLoading(false); }
  };

  const fetchMyRequests = async () => {
    setLoadingRequests(true);
    try {
      const res = await getMyQuotationRequests();
      setMyRequests(res.data);
    } catch (err) {
      toast.error(t('errorOccurred'));
    } finally { setLoadingRequests(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[#E2B05E] border-t-transparent rounded-full" /></div>;

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
        <div className="text-center">
          <img src={LOGO_URL} alt="Valhalla" className="h-16 mx-auto mb-6" />
          <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-2">Portal de Cliente</h2>
          <p className="text-[#9CA3AF] mb-6">No se encontró un perfil de cliente asociado a esta cuenta.</p>
          <button onClick={logout} className="text-[#3AB8D6] hover:underline text-sm">Cerrar Sesión</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'overview', label: isSpanish ? 'Resumen' : 'Overview', icon: CurrencyDollar },
    { key: 'projects', label: isSpanish ? 'Proyectos' : 'Projects', icon: Briefcase },
    { key: 'invoices', label: isSpanish ? 'Facturas' : 'Invoices', icon: Receipt },
    { key: 'quotations', label: isSpanish ? 'Cotizaciones' : 'Quotations', icon: FileText },
    { key: 'requests', label: isSpanish ? 'Solicitar Cotización' : 'Request Quote', icon: Plus },
  ];

  return (
    <div className="min-h-screen bg-[#0B0E14]" data-testid="client-portal">
      {/* Header */}
      <header className="border-b border-[#242E42] bg-[#0B0E14]/90 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Valhalla" className="h-8" />
            <span className="font-['Outfit'] font-bold text-white">Portal de Cliente</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#9CA3AF]">{data.client_name}</span>
            <button onClick={logout} className="text-[#9CA3AF] hover:text-red-400 transition-colors" data-testid="portal-logout"><SignOut className="w-5 h-5" /></button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-[#151B26] border border-[#242E42] rounded-xl p-5">
            <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-1">{isSpanish ? 'Proyectos Activos' : 'Active Projects'}</p>
            <p className="font-['Outfit'] text-2xl font-bold text-[#3AB8D6]">{data.summary.active_projects}</p>
          </div>
          <div className="bg-[#151B26] border border-[#242E42] rounded-xl p-5">
            <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-1">{isSpanish ? 'Total Facturado' : 'Total Invoiced'}</p>
            <p className="font-['Outfit'] text-2xl font-bold text-[#F3F4F6]">${data.summary.total_invoiced.toLocaleString()}</p>
          </div>
          <div className="bg-[#151B26] border border-[#242E42] rounded-xl p-5">
            <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-1">{isSpanish ? 'Total Pagado' : 'Total Paid'}</p>
            <p className="font-['Outfit'] text-2xl font-bold text-emerald-400">${data.summary.total_paid.toLocaleString()}</p>
          </div>
          <div className="bg-[#151B26] border border-[#242E42] rounded-xl p-5">
            <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-1">{isSpanish ? 'Pendiente' : 'Pending'}</p>
            <p className="font-['Outfit'] text-2xl font-bold text-[#E2B05E]">${data.summary.pending_payment.toLocaleString()}</p>
          </div>
          <div className="bg-[#151B26] border border-[#E2B05E]/30 rounded-xl p-5">
            <p className="text-xs text-[#E2B05E] uppercase tracking-wider mb-1">{isSpanish ? 'Mis Solicitudes' : 'My Requests'}</p>
            <p className="font-['Outfit'] text-2xl font-bold text-[#E2B05E]">{myRequests.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.key} onClick={() => { setActiveTab(tab.key); setShowNewRequest(false); setSelectedRequestId(null); }}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors rounded-lg ${
                  activeTab === tab.key ? 'bg-[#E2B05E] text-[#0B0E14]' : 'bg-[#151B26] text-[#9CA3AF] hover:text-white border border-[#242E42]'}`}
                data-testid={`portal-tab-${tab.key}`}>
                <Icon className="w-4 h-4" />{tab.label}
              </button>
            );
          })}
        </div>

        {/* Overview */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            <h3 className="font-['Outfit'] text-lg font-semibold text-[#F3F4F6]">{isSpanish ? 'Proyectos en Curso' : 'Active Projects'}</h3>
            {data.projects.filter(p => p.status === 'activo').length === 0 ? (
              <div className="bg-[#151B26] border border-[#242E42] rounded-xl p-8 text-center">
                <p className="text-[#9CA3AF]">{isSpanish ? 'No hay proyectos activos' : 'No active projects'}</p>
              </div>
            ) : (
              data.projects.filter(p => p.status === 'activo').map(proj => (
                <div key={proj._id} className="bg-[#151B26] border border-[#242E42] rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-[#F3F4F6]">{proj.name}</h4>
                    <StatusBadge value={proj.status} />
                  </div>
                  <div className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#9CA3AF]">{isSpanish ? 'Progreso' : 'Progress'}</span>
                      <span className="text-[#F3F4F6]">{proj.progress}% ({proj.completed_tasks}/{proj.total_tasks} {isSpanish ? 'tareas' : 'tasks'})</span>
                    </div>
                    <Progress value={proj.progress} className="h-2.5 bg-[#242E42]" />
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Projects */}
        {activeTab === 'projects' && (
          <div className="bg-[#151B26] border border-[#242E42] rounded-xl overflow-hidden">
            <table className="data-table">
              <thead><tr><th>{isSpanish ? 'Proyecto' : 'Project'}</th><th>{isSpanish ? 'Estado' : 'Status'}</th><th>{isSpanish ? 'Progreso' : 'Progress'}</th><th>{isSpanish ? 'Tareas' : 'Tasks'}</th></tr></thead>
              <tbody>
                {data.projects.map(p => (
                  <tr key={p._id}>
                    <td className="text-[#F3F4F6] font-medium">{p.name}</td>
                    <td><StatusBadge value={p.status} /></td>
                    <td><div className="flex items-center gap-2"><Progress value={p.progress} className="h-2 w-24 bg-[#242E42]" /><span className="text-sm text-[#9CA3AF]">{p.progress}%</span></div></td>
                    <td className="text-[#9CA3AF]">{p.completed_tasks}/{p.total_tasks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Invoices */}
        {activeTab === 'invoices' && (
          <div className="bg-[#151B26] border border-[#242E42] rounded-xl overflow-hidden">
            <table className="data-table">
              <thead><tr><th>{isSpanish ? 'Factura' : 'Invoice'}</th><th>{isSpanish ? 'Fecha' : 'Date'}</th><th>{isSpanish ? 'Vencimiento' : 'Due'}</th><th>Total</th><th>{isSpanish ? 'Estado' : 'Status'}</th></tr></thead>
              <tbody>
                {data.invoices.map(inv => (
                  <tr key={inv._id}>
                    <td className="text-[#E2B05E] font-medium">{inv.invoice_number}</td>
                    <td className="text-[#9CA3AF]">{inv.issue_date}</td>
                    <td className="text-[#9CA3AF]">{inv.due_date}</td>
                    <td className="font-['Outfit'] font-bold text-[#F3F4F6]">${inv.total?.toFixed(2)}</td>
                    <td><StatusBadge value={inv.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Quotations */}
        {activeTab === 'quotations' && (
          <div className="bg-[#151B26] border border-[#242E42] rounded-xl overflow-hidden">
            <table className="data-table">
              <thead><tr><th>{isSpanish ? 'Cotización' : 'Quotation'}</th><th>{isSpanish ? 'Fecha' : 'Date'}</th><th>Total</th><th>{isSpanish ? 'Estado' : 'Status'}</th></tr></thead>
              <tbody>
                {data.quotations.map(q => (
                  <tr key={q._id}>
                    <td className="text-[#E2B05E] font-medium">{q.quotation_number}</td>
                    <td className="text-[#9CA3AF]">{q.issue_date}</td>
                    <td className="font-['Outfit'] font-bold text-[#F3F4F6]">${q.total?.toFixed(2)}</td>
                    <td><StatusBadge value={q.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Quotation Requests */}
        {activeTab === 'requests' && (
          <>
            {selectedRequestId ? (
              <RequestDetailView requestId={selectedRequestId} onBack={() => setSelectedRequestId(null)} />
            ) : showNewRequest ? (
              <div className="bg-[#151B26] border border-[#242E42] rounded-xl p-6">
                <h3 className="font-['Outfit'] text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-[#E2B05E]" />
                  {isSpanish ? 'Nueva Solicitud de Cotización' : 'New Quotation Request'}
                </h3>
                <NewRequestForm 
                  onSuccess={() => { setShowNewRequest(false); fetchMyRequests(); }}
                  onCancel={() => setShowNewRequest(false)}
                  clientName={data.client_name}
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-['Outfit'] text-lg font-semibold text-[#F3F4F6]">{isSpanish ? 'Mis Solicitudes' : 'My Requests'}</h3>
                  <Button onClick={() => setShowNewRequest(true)} className="bg-[#E2B05E] text-[#0B0E14] hover:bg-[#F0C47A]">
                    <Plus className="w-4 h-4 mr-2" />
                    {isSpanish ? 'Nueva Solicitud' : 'New Request'}
                  </Button>
                </div>

                {loadingRequests ? (
                  <div className="text-center py-8"><div className="animate-spin w-8 h-8 border-2 border-[#E2B05E] border-t-transparent rounded-full mx-auto" /></div>
                ) : myRequests.length === 0 ? (
                  <div className="bg-[#151B26] border border-[#242E42] rounded-xl p-12 text-center">
                    <FileText className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" />
                    <p className="text-[#9CA3AF] mb-4">{isSpanish ? 'No tienes solicitudes aún' : 'You have no requests yet'}</p>
                    <Button onClick={() => setShowNewRequest(true)} className="bg-[#E2B05E] text-[#0B0E14] hover:bg-[#F0C47A]">
                      <Plus className="w-4 h-4 mr-2" />
                      {isSpanish ? 'Crear Primera Solicitud' : 'Create First Request'}
                    </Button>
                  </div>
                ) : (
                  <div className="bg-[#151B26] border border-[#242E42] rounded-xl overflow-hidden">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{isSpanish ? 'Número' : 'Number'}</th>
                          <th>{isSpanish ? 'Título' : 'Title'}</th>
                          <th>{isSpanish ? 'Tipo' : 'Type'}</th>
                          <th>{isSpanish ? 'Estado' : 'Status'}</th>
                          <th>{isSpanish ? 'Cotización' : 'Quotation'}</th>
                          <th>{isSpanish ? 'Fecha' : 'Date'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {myRequests.map(req => (
                          <tr key={req._id} className="cursor-pointer hover:bg-[#0B0E14]" onClick={() => setSelectedRequestId(req._id)}>
                            <td className="text-[#E2B05E] font-mono text-sm">{req.request_number}</td>
                            <td className="text-[#F3F4F6] font-medium">{req.title}</td>
                            <td className="text-[#9CA3AF]">{req.service_type}</td>
                            <td><StatusBadge value={req.status} /></td>
                            <td className="text-[#3AB8D6]">{req.quotation_number || '-'}</td>
                            <td className="text-[#9CA3AF]">{req.created_at?.split('T')[0]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default ClientPortalPage;
